import { Component, OnInit, OnDestroy, ElementRef, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TourGuideService, TourStep } from '../../services/tour-guide.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-tour-guide',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div *ngIf="isActive" class="tour-overlay" (click)="onOverlayClick()">
      <!-- Backdrop oscurato con SVG per creare il "buco" -->
      <svg class="tour-backdrop-svg" width="100%" height="100%">
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white"/>
            <rect 
              *ngIf="currentStep && targetElement"
              [attr.x]="highlightPosition.left - 5"
              [attr.y]="highlightPosition.top - 5"
              [attr.width]="highlightPosition.width + 10"
              [attr.height]="highlightPosition.height + 10"
              [attr.rx]="12"
              fill="black"/>
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" mask="url(#tour-mask)" />
      </svg>
      
      <!-- Highlight dell'elemento corrente -->
      <div 
        *ngIf="currentStep && targetElement"
        class="tour-highlight"
        [style.top.px]="highlightPosition.top"
        [style.left.px]="highlightPosition.left"
        [style.width.px]="highlightPosition.width"
        [style.height.px]="highlightPosition.height">
      </div>

      <!-- Tooltip con contenuto -->
      <div 
        *ngIf="currentStep && targetElement"
        class="tour-tooltip"
        [ngClass]="'tour-tooltip-' + currentStep.position"
        [style.top.px]="tooltipPosition.top"
        [style.left.px]="tooltipPosition.left">
        
        <div class="tour-tooltip-header">
          <h4 class="tour-title">{{ currentStep.title }}</h4>
          <div class="tour-progress">
            <span class="tour-step-counter">
              {{ stepIndex + 1 }} / {{ totalSteps }}
            </span>
          </div>
        </div>

        <div class="tour-tooltip-body">
          <p [innerHTML]="currentStep.content"></p>
        </div>

        <div class="tour-tooltip-footer">
          <div class="tour-buttons-left">
            <button 
              *ngIf="currentStep.showSkip"
              type="button" 
              class="btn btn-outline-secondary btn-sm"
              (click)="skipTour()">
              <i class="fas fa-times me-1"></i>
              {{ 'TOUR.SKIP' | translate }}
            </button>
          </div>

          <div class="tour-buttons-right">
            <button 
              *ngIf="currentStep.showPrev && stepIndex > 0"
              type="button" 
              class="btn btn-outline-primary btn-sm me-2"
              (click)="prevStep()">
              <i class="fas fa-chevron-left me-1"></i>
              {{ 'TOUR.PREVIOUS' | translate }}
            </button>
            
            <button 
              *ngIf="currentStep.showNext"
              type="button" 
              class="btn btn-primary btn-sm"
              (click)="nextStep()">
              {{ stepIndex < totalSteps - 1 ? ('TOUR.NEXT' | translate) : ('TOUR.FINISH' | translate) }}
              <i class="fas" [ngClass]="stepIndex < totalSteps - 1 ? 'fa-chevron-right ms-1' : 'fa-check ms-1'"></i>
            </button>
          </div>
        </div>

        <!-- Freccia del tooltip -->
        <div class="tour-tooltip-arrow" [ngClass]="'arrow-' + currentStep.position"></div>
      </div>

      <!-- Indicatori di progresso -->
      <div *ngIf="totalSteps > 1" class="tour-progress-dots">
        <button 
          *ngFor="let step of steps; let i = index"
          class="tour-dot"
          [ngClass]="{
            'tour-dot-active': i === stepIndex,
            'tour-dot-completed': i < stepIndex
          }"
          (click)="goToStep(i)"
          [title]="step.title">
        </button>
      </div>
    </div>

    <!-- Indicatore fisso nella parte inferiore -->
    <div *ngIf="isActive" class="tour-bottom-indicator">
      <div class="tour-indicator-content">
        <div class="tour-step-info">
          <span class="tour-step-text">{{ 'TOUR.STEP' | translate }}</span>
          <span class="tour-step-numbers">{{ stepIndex + 1 }} / {{ totalSteps }}</span>
        </div>
        <button 
          type="button" 
          class="btn btn-outline-danger btn-sm tour-quick-close"
          (click)="skipTour()"
          [title]="'TOUR.QUICK_CLOSE' | translate">
          <i class="fas fa-times"></i>
          {{ 'TOUR.CLOSE' | translate }}
        </button>
      </div>
    </div>

    <!-- Messaggio di completamento tour -->
    <div *ngIf="showCompletionMessage" class="tour-completion-overlay">
      <div class="tour-completion-modal">
        <div class="tour-completion-header">
          <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
          <h3>{{ 'TOUR.COMPLETED_TITLE' | translate }}</h3>
        </div>
        <div class="tour-completion-body">
          <p>{{ 'TOUR.COMPLETED_MESSAGE' | translate }}</p>
        </div>
        <div class="tour-completion-footer">
          <button 
            type="button" 
            class="btn btn-primary"
            (click)="closeCompletionMessage()">
            <i class="fas fa-check me-1"></i>
            {{ 'TOUR.CLOSE' | translate }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./tour-guide.component.css']
})
export class TourGuideComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isActive = false;
  currentStep: TourStep | null = null;
  stepIndex = 0;
  totalSteps = 0;
  steps: TourStep[] = [];
  showCompletionMessage = false;

  targetElement: HTMLElement | null = null;
  highlightPosition = { top: 0, left: 0, width: 0, height: 0 };
  tooltipPosition = { top: 0, left: 0 };

  constructor(
    private tourGuideService: TourGuideService,
    private elementRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.tourGuideService.tourState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isActive = state.isActive;
        this.currentStep = state.currentStep;
        this.stepIndex = state.stepIndex;
        this.totalSteps = state.totalSteps;
        
        if (this.isActive && this.currentStep) {
          setTimeout(() => this.updatePositions(), 100);
        } else {
          this.resetTargetElement();
        }

        // Mostra il messaggio di completamento quando il tour finisce
        if (!this.isActive && this.stepIndex >= this.totalSteps - 1 && this.totalSteps > 0 && !this.showCompletionMessage) {
          setTimeout(() => {
            this.showCompletionMessage = true;
          }, 300);
        }
      });

    // Ottieni gli steps dal servizio
    const currentTour = this.tourGuideService.getCurrentTour();
    if (currentTour) {
      this.steps = currentTour.steps;
    }

    // Aggiorna posizioni quando la finestra viene ridimensionata
    window.addEventListener('resize', () => this.updatePositions());
    window.addEventListener('scroll', () => this.updatePositions());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', () => this.updatePositions());
    window.removeEventListener('scroll', () => this.updatePositions());
    
    // Resetta lo z-index dell'elemento target quando il componente viene distrutto
    this.resetTargetElement();
  }

  private updatePositions(): void {
    if (!this.currentStep) return;

    const element = document.querySelector(this.currentStep.selector) as HTMLElement;
    if (!element) return;

    // Reset previous target element
    this.resetTargetElement();
    
    this.targetElement = element;
    
    // Strategia più robusta per garantire la visibilità
    this.makeElementVisible(element);

    const rect = element.getBoundingClientRect();
    const padding = 8;    // Posizione dell'highlight
    this.highlightPosition = {
      top: rect.top - padding,
      left: rect.left - padding,
      width: rect.width + (padding * 2),
      height: rect.height + (padding * 2)
    };

    // Posizione del tooltip
    this.calculateTooltipPosition(rect);
  }

  private calculateTooltipPosition(targetRect: DOMRect): void {
    const tooltipWidth = 450; // Aggiornato per corrispondere al CSS
    const tooltipHeight = 280; // Aumentato per contenere più contenuto
    const margin = 20;
    const viewportMargin = 20;

    let top = 0;
    let left = 0;
    let position = this.currentStep?.position || 'bottom';

    // Calcola la posizione iniziale
    switch (position) {
      case 'top':
        top = targetRect.top - tooltipHeight - margin;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = targetRect.bottom + margin;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left - tooltipWidth - margin;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.right + margin;
        break;
    }

    // Se il tooltip va fuori schermo orizzontalmente, prova a posizionarlo sopra o sotto
    if (left < viewportMargin || left + tooltipWidth > window.innerWidth - viewportMargin) {
      if (position === 'left' || position === 'right') {
        // Cambia da posizione laterale a sopra/sotto
        if (targetRect.bottom + tooltipHeight + margin < window.innerHeight - viewportMargin) {
          // Posiziona sotto
          top = targetRect.bottom + margin;
          left = Math.max(viewportMargin, Math.min(
            targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2),
            window.innerWidth - tooltipWidth - viewportMargin
          ));
        } else {
          // Posiziona sopra
          top = targetRect.top - tooltipHeight - margin;
          left = Math.max(viewportMargin, Math.min(
            targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2),
            window.innerWidth - tooltipWidth - viewportMargin
          ));
        }
      }
    }

    // Controllo finale per assicurarsi che rimanga nel viewport
    if (left < viewportMargin) {
      left = viewportMargin;
    }
    if (left + tooltipWidth > window.innerWidth - viewportMargin) {
      left = window.innerWidth - tooltipWidth - viewportMargin;
    }
    
    if (top < viewportMargin) {
      top = viewportMargin;
    }
    if (top + tooltipHeight > window.innerHeight - viewportMargin) {
      top = window.innerHeight - tooltipHeight - viewportMargin;
    }

    // Fallback di sicurezza
    if (left < 0) left = viewportMargin;
    if (top < 0) top = viewportMargin;

    this.tooltipPosition = { top, left };
  }

  nextStep(): void {
    this.tourGuideService.nextStep();
  }

  prevStep(): void {
    this.tourGuideService.prevStep();
  }

  skipTour(): void {
    this.resetTargetElement();
    this.showCompletionMessage = false;
    this.tourGuideService.skipTour();
  }

  goToStep(stepIndex: number): void {
    this.tourGuideService.goToStep(stepIndex);
  }

  closeCompletionMessage(): void {
    this.showCompletionMessage = false;
  }

  onOverlayClick(): void {
    // Opzionalmente permetti di chiudere cliccando sull'overlay
    // this.skipTour();
  }

  private resetTargetElement(): void {
    if (this.targetElement) {
      // Reset l'elemento principale - ripristina solo se era static
      const originalPosition = this.targetElement.getAttribute('data-original-position');
      if (originalPosition === 'static' || !originalPosition) {
        this.targetElement.style.position = '';
      }
      this.targetElement.style.zIndex = '';
      this.targetElement.style.transform = '';
      this.targetElement.classList.remove('tour-highlighted-element');
      this.targetElement.removeAttribute('data-original-position');
      
      // Reset SOLO z-index per i sotto-elementi
      const childElements = this.targetElement.querySelectorAll('*') as NodeListOf<HTMLElement>;
      childElements.forEach(child => {
        child.style.zIndex = '';
        child.style.transform = '';
        // NON toccare position per i figli
      });
      
      // Reset del container padre
      const parentContainer = this.targetElement.closest('.input-group, .form-floating, .position-relative') as HTMLElement;
      if (parentContainer && parentContainer !== this.targetElement) {
        const parentOriginalPosition = parentContainer.getAttribute('data-original-position');
        if (parentOriginalPosition === 'static' || !parentOriginalPosition) {
          parentContainer.style.position = '';
        }
        parentContainer.style.zIndex = '';
        parentContainer.removeAttribute('data-original-position');
      }
      
      // Reset dei wrapper
      const wrappers = this.targetElement.closest('.form-group, .mb-3, .col-md-6, .col-lg-4') as HTMLElement;
      if (wrappers) {
        wrappers.style.zIndex = '';
      }
      
      this.targetElement = null;
    }
  }

  private makeElementVisible(element: HTMLElement): void {
    // Salva e applica z-index all'elemento principale SENZA cambiare position
    const originalPosition = window.getComputedStyle(element).position;
    element.setAttribute('data-original-position', originalPosition);
    
    if (originalPosition === 'static') {
      element.style.position = 'relative';
    }
    element.style.zIndex = '10005';
    element.classList.add('tour-highlighted-element');
    
    // Gestione del container padre per input groups
    const parentContainer = element.closest('.input-group, .form-floating, .position-relative') as HTMLElement;
    if (parentContainer && parentContainer !== element) {
      const parentOriginalPosition = window.getComputedStyle(parentContainer).position;
      parentContainer.setAttribute('data-original-position', parentOriginalPosition);
      
      if (parentOriginalPosition === 'static') {
        parentContainer.style.position = 'relative';
      }
      parentContainer.style.zIndex = '10005';
    }
    
    // Per tutti i discendenti, applica SOLO z-index, MAI position
    const allDescendants = element.querySelectorAll('*') as NodeListOf<HTMLElement>;
    allDescendants.forEach(child => {
      // Usa solo z-index senza toccare position
      child.style.zIndex = '10006';
      
      // Per le icone, z-index più alto
      if (child.tagName === 'I' || 
          child.classList.contains('fas') || 
          child.classList.contains('far') || 
          child.classList.contains('fab') || 
          child.classList.contains('input-group-text')) {
        child.style.zIndex = '10007';
      }
    });
    
    // Non forzare position sui wrapper
    const wrappers = element.closest('.form-group, .mb-3, .col-md-6, .col-lg-4') as HTMLElement;
    if (wrappers) {
      wrappers.style.zIndex = '10004';
    }
  }
}
